import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "@tanstack/react-router";
import {
  Calendar,
  MapPin,
  Users,
  Tag,
  ArrowLeft,
  Share2,
  BarChart3,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  LogIn,
  User,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { V1BEClient } from "@/clients";
import {
  countSuppliers,
  formatDateRange,
  getImageOrAcronym,
  getLocation,
} from "@/lib/utils";
import {
  IEventCheckInButtonState,
  IOrganizationEvent,
} from "@/types/EventTypes";
import { useAuthStore } from "../../store/authStore";
import DialogConfirm from "../ui/form/DialogConfirm";
import ShareEvent from "../ui/ShareEvent";
import { EventSuppliers } from "./suppliers";

const EventDetails = () => {
  const { eventId } = useParams({ from: "/events/$eventId" });
  const [orgEvent, setOrgEvent] = useState<IOrganizationEvent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { userDetails, isLoggedIn } = useAuthStore();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const navigate = useNavigate();
  const defaultCheckInButtonState: IEventCheckInButtonState = {
    disabled: false,
    text: "Sign In to Check In",
    icon: <LogIn size={20} />,
    className:
      "w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium flex items-center justify-center gap-2",
    message: "You must sign in to check in for this event.",
    statusType: "auth-required",
    statusTitle: "Authentication Required",
    statusMessage: "You must sign in to access check-in functionality.",
    statusAction: {
      text: "Sign In",
      link: "/login",
      external: false,
    },
  };
  const [checkInButtonState, setCheckInButtonState] =
    useState<IEventCheckInButtonState>(defaultCheckInButtonState);
  const [hasProducts, setHasProducts] = useState<boolean>(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Type guard function to safely check user type
  const isVerifiedSupplierType = (
    type: string
  ): type is "verified-supplier" => {
    return type === "verified-supplier";
  };

  const load = async (showLoading = true) => {
    if (!eventId) return;
    if (showLoading) setIsLoading(true);
    const data = await V1BEClient.getOrganizationEvent(eventId);
    setOrgEvent(data);

    if (showLoading) setIsLoading(false);
  };

  useEffect(() => {
    load();
  }, [eventId]);

  useEffect(() => {
    // Check if current user has already checked in
    if (orgEvent && userDetails) {
      const isAttending = orgEvent.organization_event_attendees?.some(
        (attendee) => attendee.user_id === userDetails.user_id
      );

      handleButtonState(Boolean(isAttending));
    }
  }, [userDetails, orgEvent]);

  const handleButtonState = (isAttending: boolean) => {
    if (!isLoggedIn) {
      setCheckInButtonState(defaultCheckInButtonState);
      return;
    }

    // If user has already checked in, show thank you message
    if (isAttending) {
      setCheckInButtonState({
        disabled: true,
        text: "Already Checked In",
        icon: <CheckCircle size={20} />,
        className:
          "w-full bg-green-600 text-white py-3 px-6 rounded-lg cursor-not-allowed font-medium flex items-center justify-center gap-2",
        message: "You have successfully checked in to this event.",
        statusType: "verified",
        statusTitle: "Thank You!",
        statusMessage:
          "Thank you for confirming your attendance. We look forward to seeing you at the event!",
        statusAction: null,
      });
      return;
    }

    if (userDetails) {
      if (userDetails.user_type === 2) {
        setCheckInButtonState({
          disabled: false,
          text: "Check In as Guest",
          icon: <User size={20} />,
          className:
            "w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center gap-2",
          message: "Check in to attend the event and find suppliers.",
          statusType: "guest-allowed",
          statusTitle: "Welcome Guest!",
          statusMessage:
            "You can check in to attend this event and discover wedding suppliers. This is perfect for couples planning their wedding.",
          statusAction: null,
        });
        return;
      }

      if (userDetails.user_type === 3) {
        if (userDetails.verification?.status === "verified") {
          V1BEClient.fetchMyProducts().then((res) => {
            if (res.data.length > 0) {
              setHasProducts(true);
              setCheckInButtonState({
                disabled: false,
                text: "Check In as Supplier",
                icon: <CheckCircle size={20} />,
                className:
                  "w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium flex items-center justify-center gap-2",
                message: "Check in to attend the event and find customers.",
                statusType: "verified",
                statusTitle: "Verified Supplier",
                statusMessage:
                  "You're eligible to check in as a verified supplier for this event.",
                statusAction: null,
              });
            } else {
              setHasProducts(false);
              setCheckInButtonState({
                disabled: true,
                text: "Product Required",
                icon: <AlertCircle size={20} />,
                className:
                  "w-full bg-gray-400 text-white py-3 px-6 rounded-lg cursor-not-allowed font-medium flex items-center justify-center gap-2",
                message:
                  "You must have at least one active product listing to check in.",
                statusType: "verification-required",
                statusTitle: "Product Required",
                statusMessage:
                  "You must have at least one active product listing to check in.",
                statusAction: {
                  text: "List your product/service on Evently",
                  link: "https://evently.ph",
                  external: true,
                },
              });
            }
          });
          return;
        }

        if (userDetails.verification?.status === "pending") {
          setCheckInButtonState({
            disabled: true,
            text: "Check In as Supplier",
            icon: <CheckCircle size={20} />,
            className:
              "w-full bg-gray-400 text-white py-3 px-6 rounded-lg cursor-not-allowed font-medium flex items-center justify-center gap-2",
            message: "Your verification is pending. Please wait for approval.",
            statusType: "verification-required",
            statusTitle: "Verification Required",
            statusMessage:
              "Your verification is pending. Please wait for approval.",
            statusAction: {
              text: "List your product/service on Evently",
              link: "https://evently.ph",
              external: true,
            },
          });
          return;
        }

        setCheckInButtonState({
          disabled: true,
          text: "Check In as Supplier",
          icon: <AlertCircle size={20} />,
          className:
            "w-full bg-gray-400 text-white py-3 px-6 rounded-lg cursor-not-allowed font-medium flex items-center justify-center gap-2",
          message:
            "You must be a verified supplier with an active listing on Evently to check in.",
          statusType: "verification-required",
          statusTitle: "Verification Required",
          statusMessage:
            "You must be a verified supplier with an active listing on Evently to check in.",
          statusAction: {
            text: "Request to become a verified supplier on Evently",
            link: "https://evently.ph",
            external: true,
          },
        });
        return;
      }
    }

    setCheckInButtonState(defaultCheckInButtonState);
  };

  const handleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleCheckIn = () => {
    if (!isLoggedIn) {
      navigate({ to: "/login" });
      return;
    }

    if (!orgEvent || !userDetails) return;
    setIsConfirmationOpen(true);
  };

  const confirmCheckIn = async () => {
    setIsCheckingIn(true);

    try {
      const attendingAs = userDetails.user_type === 2 ? "guest" : "supplier";

      const response = await V1BEClient.confirmOrganizationEventAttendance({
        organization_event_id: orgEvent?.id || "",
        attending_as: attendingAs,
        status: "attending",
      });

      if (response) {
        toast.success("Successfully checked in!");
        handleButtonState(true); // Mark user as checked in
        await load(false); // Refresh event details to update attendee count/list without full page reload
      } else {
        toast.error("Failed to check in. Please try again.");
      }
    } catch (error) {
      console.error("Check-in error:", error);
      toast.error("An error occurred during check-in.");
    } finally {
      setIsCheckingIn(false);
      setIsConfirmationOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading wedding event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!orgEvent) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Wedding Event Not Found
            </h1>
            <p className="text-gray-600 mb-8">
              The wedding event you're looking for doesn't exist or has been
              removed.
            </p>
            <Link
              to="/events"
              className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors duration-200"
            >
              Back to Wedding Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Back Button */}
        <div className="mb-6">
          <Link
            to="/events"
            className="inline-flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Wedding Events
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Image */}
            <div className="relative">
              {orgEvent.image_url ? (
                <img
                  src={orgEvent.image_url}
                  alt={orgEvent.name}
                  className="w-full h-full rounded-lg"
                />
              ) : (
                <div className="w-full h-96 rounded-lg bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-5xl">
                    {getImageOrAcronym(orgEvent.name, orgEvent.image_url).text}
                  </span>
                </div>
              )}
              <div className="absolute top-4 left-4">
                <span className="bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                  {orgEvent.organization?.name || "Event"}
                </span>
              </div>
              <div className="absolute top-4 right-4 flex space-x-2">
                <button
                  onClick={handleShare}
                  className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <Share2 size={20} />
                </button>
              </div>
            </div>

            {/* Event Title and Description */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                {orgEvent.name}
              </h1>
              <div
                className="event-description mb-6"
                dangerouslySetInnerHTML={{ __html: orgEvent.description || "" }}
              />
              <style>{`
                .event-description {
                  color: #4b5563;
                  font-size: 1rem;
                  line-height: 1.75;
                }
                .event-description h1 {
                  color: #111827;
                  font-size: 1.5rem;
                  font-weight: 700;
                  margin-bottom: 1rem;
                }
                .event-description h2 {
                  color: #111827;
                  font-size: 1.25rem;
                  font-weight: 700;
                  margin-bottom: 0.75rem;
                }
                .event-description h3 {
                  color: #111827;
                  font-size: 1.125rem;
                  font-weight: 700;
                  margin-bottom: 0.5rem;
                }
                .event-description p {
                  color: #4b5563;
                  margin-bottom: 1rem;
                }
                .event-description ul {
                  list-style-type: disc;
                  padding-left: 1.5rem;
                  margin-bottom: 1rem;
                  color: #4b5563;
                }
                .event-description ol {
                  list-style-type: decimal;
                  padding-left: 1.5rem;
                  margin-bottom: 1rem;
                  color: #4b5563;
                }
                .event-description li {
                  margin-bottom: 0.5rem;
                }
                .event-description strong {
                  color: #111827;
                  font-weight: 600;
                }
                .event-description em {
                  font-style: italic;
                }
                .event-description a {
                  color: #dc2626;
                  text-decoration: underline;
                }
                .event-description a:hover {
                  color: #b91c1c;
                }
                .event-description blockquote {
                  border-left: 4px solid #fecaca;
                  padding-left: 1rem;
                  font-style: italic;
                  color: #4b5563;
                  margin-bottom: 1rem;
                }
                .event-description code {
                  background-color: #f3f4f6;
                  padding: 0.125rem 0.25rem;
                  border-radius: 0.25rem;
                  font-size: 0.875rem;
                }
              `}</style>

              {/* Event Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <Calendar className="text-primary-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Date</p>
                    <p className="text-gray-600">
                      {formatDateRange(orgEvent.date_from, orgEvent.date_to)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <MapPin className="text-primary-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Location</p>
                    <p className="text-gray-600">
                      {getLocation(orgEvent.organization_event_string_properties || [])}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Users className="text-primary-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Registered</p>
                    <p className="text-gray-600">
                      {countSuppliers(orgEvent.organization_event_attendees || [])}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Tag className="text-primary-600" size={20} />
                  <div>
                    <p className="font-medium text-gray-900">Organizer</p>
                    <p className="text-gray-600">
                      {orgEvent.organization?.name || "—"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <EventSuppliers organizationEventId={orgEvent.id} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Supplier Check-in Card */}
            <div className="bg-white rounded-lg shadow-lg p-6 top-24">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {orgEvent.attendees?.length ??
                    orgEvent.organization_event_attendees?.length ??
                    0}
                </div>
                <p className="text-gray-600">Registered Attendees</p>
              </div>

              <div className="space-y-4 mb-6"></div>

              {/* Check-in Button - Dynamic based on user state */}
              <div className="space-y-4">
                <button
                  onClick={handleCheckIn}
                  disabled={checkInButtonState.disabled || isCheckingIn}
                  className={checkInButtonState.className}
                >
                  {isCheckingIn ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    checkInButtonState.icon
                  )}
                  {isCheckingIn ? "Checking In..." : checkInButtonState.text}
                </button>

                {/* Status Message */}
                <div
                  className={`rounded-lg p-4 ${
                    checkInButtonState.statusType === "verified"
                      ? "bg-green-50 border border-green-200"
                      : checkInButtonState.statusType === "auth-required"
                        ? "bg-blue-50 border border-blue-200"
                        : checkInButtonState.statusType === "guest-allowed"
                          ? "bg-blue-50 border border-blue-200"
                          : checkInButtonState.statusType ===
                              "verification-required"
                            ? "bg-amber-50 border border-amber-200"
                            : "bg-amber-50 border border-amber-200"
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {checkInButtonState.statusType === "verified" && (
                      <CheckCircle
                        className="text-green-600 mt-0.5"
                        size={20}
                      />
                    )}
                    {checkInButtonState.statusType === "auth-required" && (
                      <LogIn className="text-blue-600 mt-0.5" size={20} />
                    )}
                    {checkInButtonState.statusType === "guest-allowed" && (
                      <User className="text-blue-600 mt-0.5" size={20} />
                    )}
                    {checkInButtonState.statusType ===
                      "verification-required" && (
                      <AlertCircle
                        className="text-amber-600 mt-0.5"
                        size={20}
                      />
                    )}

                    <div className="text-sm">
                      <p
                        className={`font-medium mb-2 ${
                          checkInButtonState.statusType === "verified"
                            ? "text-green-800"
                            : checkInButtonState.statusType === "auth-required"
                              ? "text-blue-800"
                              : checkInButtonState.statusType ===
                                  "guest-allowed"
                                ? "text-blue-800"
                                : checkInButtonState.statusType ===
                                    "verification-required"
                                  ? "text-amber-800"
                                  : "text-amber-800"
                        }`}
                      >
                        {checkInButtonState.statusTitle}
                      </p>
                      <p
                        className={`mb-3 ${
                          checkInButtonState.statusType === "verified"
                            ? "text-green-700"
                            : checkInButtonState.statusType === "auth-required"
                              ? "text-blue-700"
                              : checkInButtonState.statusType ===
                                  "guest-allowed"
                                ? "text-blue-700"
                                : checkInButtonState.statusType ===
                                    "verification-required"
                                  ? "text-amber-700"
                                  : "text-amber-700"
                        }`}
                      >
                        {checkInButtonState.statusMessage}
                      </p>

                      {checkInButtonState.statusAction &&
                        (checkInButtonState.statusAction.external ? (
                          <a
                            href={checkInButtonState.statusAction.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center font-medium ${
                              checkInButtonState.statusType === "auth-required"
                                ? "text-blue-800 hover:text-blue-900"
                                : checkInButtonState.statusType ===
                                    "guest-allowed"
                                  ? "text-blue-800 hover:text-blue-900"
                                  : checkInButtonState.statusType ===
                                      "verification-required"
                                    ? "text-amber-800 hover:text-amber-900"
                                    : "text-amber-800 hover:text-amber-900"
                            }`}
                          >
                            {checkInButtonState.statusAction.text}
                            <ExternalLink size={16} className="ml-1" />
                          </a>
                        ) : (
                          <Link
                            to={checkInButtonState.statusAction.link as any}
                            className={`inline-flex items-center font-medium ${
                              checkInButtonState.statusType === "auth-required"
                                ? "text-blue-800 hover:text-blue-900"
                                : checkInButtonState.statusType ===
                                    "guest-allowed"
                                  ? "text-blue-800 hover:text-blue-900"
                                  : checkInButtonState.statusType ===
                                      "verification-required"
                                    ? "text-amber-800 hover:text-amber-900"
                                    : "text-amber-800 hover:text-amber-900"
                            }`}
                          >
                            {checkInButtonState.statusAction.text}
                          </Link>
                        ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <p className="text-sm text-gray-500">
                  Need help?{" "}
                  <Link
                    to="/contact"
                    className="text-primary-600 hover:text-primary-700"
                  >
                    Contact us
                  </Link>
                </p>
              </div>
            </div>

            {/* Dashboard Button */}
            {userDetails.user_level >= 99 && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Event Analytics
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  View detailed dashboard and performance metrics for this
                  event.
                </p>
                <Link
                  to="/dashboard"
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium flex items-center justify-center gap-2"
                >
                  <BarChart3 size={18} />
                  View Dashboard
                </Link>
              </div>
            )}

            {/* Organizer Info */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Organizer
              </h3>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-600 font-bold text-lg">P</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    PhilTrade Weddings
                  </p>
                  <p className="text-sm text-gray-600">
                    Professional Wedding Planners
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <p className="text-sm text-gray-600">
                  <strong>Contact:</strong> weddings@philtrade.com
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Phone:</strong> +63 2 1234 5678
                </p>
              </div>
            </div>

            {/* Similar Events */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Similar Events
              </h3>
              <p className="text-sm text-gray-600">
                Discover more {orgEvent.organization?.name || "wedding"} events
                and showcases.
              </p>
              <Link
                to="/events"
                className="block mt-4 text-primary-600 hover:text-primary-700 font-medium"
              >
                Browse all events →
              </Link>
            </div>
          </div>
        </div>
        <DialogConfirm
          open={isConfirmationOpen}
          onOpenChange={setIsConfirmationOpen}
          onConfirm={confirmCheckIn}
          title="Confirm Attendance"
          description={
            <>
              You are about to check in to{" "}
              <span className="font-semibold text-gray-900">
                {orgEvent?.name}
              </span>
              as a {userDetails?.user_type === 2 ? "guest" : "supplier"}. Are
              you sure you want to proceed?
            </>
          }
          isLoading={isCheckingIn}
          loadingText="Checking In..."
        />
        {showShareModal && orgEvent && (
          <ShareEvent
            open={showShareModal}
            onOpenChange={setShowShareModal}
            event={orgEvent}
          />
        )}
      </div>
    </div>
  );
};

export default EventDetails;
